<template>
    <div class="planner-root" ref="root">

        <!-- ══ TOP BAR ══ -->
        <header class="top-bar">
            <div class="tb-view-switcher">
                <button class="tb-view-btn" :class="{ active: viewMode === 'day' }"   @click="viewMode = 'day'"><i class="fas fa-calendar-day"></i><span>Jour</span></button>
                <button class="tb-view-btn" :class="{ active: viewMode === 'week' }"  @click="viewMode = 'week'"><i class="fas fa-calendar-week"></i><span>Semaine</span></button>
                <button class="tb-view-btn" :class="{ active: viewMode === 'month' }" @click="viewMode = 'month'"><i class="fas fa-calendar"></i><span>Mois</span></button>
            </div>

            <div v-if="technicians.length > 1" class="tb-tech-filter" ref="techFilter">
                <div class="custom-select" :class="{ open: selectOpen }"
                    @mouseenter="onEnter"
                    @mouseleave="onLeave">
                    <div class="cs-trigger" ref="csTrigger">
                        <span class="cs-dot" :style="selectedTech ? { background: selectedTech.color } : {}"></span>
                        <span class="cs-label">{{ selectedTech ? selectedTech.name : 'Tous les techniciens' }}</span>
                        <i class="fas fa-chevron-down cs-arrow"></i>
                    </div>
                </div>

                <!-- Dropdown monté sur body via JS, hors du flux -->
                <div v-if="selectOpen"
                    class="cs-dropdown-portal"
                    :style="dropdownStyle"
                    @mouseenter="onEnter"
                    @mouseleave="onLeave">
                    <div class="cs-option" :class="{ selected: filterTechId === null }"
                        @click="filterTechId = null; selectOpen = false">
                        <span class="cs-dot cs-dot-all"></span>
                        <span>Tous les techniciens</span>
                    </div>
                    <div v-for="t in technicians" :key="'tfc-' + t.id"
                        class="cs-option" :class="{ selected: filterTechId === t.id }"
                        @click="filterTechId = t.id; selectOpen = false">
                        <span class="cs-dot" :style="{ background: t.color }"></span>
                        <span>{{ t.name }}</span>
                    </div>
                </div>
            </div>

            <!-- ── Badge multi-sélection ── -->
            <transition name="ms-badge-slide">
                <div v-if="multiSlots && selectedSlots.length > 0" class="ms-selection-badge">
                    <i class="fas fa-layer-group ms-badge-icon"></i>
                    <span class="ms-badge-count">{{ selectedSlots.length }}</span>
                    <span class="ms-badge-label">slot{{ selectedSlots.length > 1 ? 's' : '' }} sélectionné{{ selectedSlots.length > 1 ? 's' : '' }}</span>
                    <button class="ms-badge-clear" @click="clearSelection" title="Désélectionner tout">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            </transition>

            <!-- Date Navigator -->
            <div class="tb-date-nav" ref="dateNav">
                <button class="tb-date-prev" @click="offsetPeriod(-1)"><i class="fas fa-chevron-left"></i></button>
                <button class="tb-date-trigger" @click="toggleDatePicker">
                    <i class="fas fa-calendar-alt tb-date-icon"></i>
                    <span class="tb-date-label">{{ dateRangeLabel }}</span>
                    <i class="fas fa-chevron-down tb-date-arrow" :class="{ open: datePickerOpen }"></i>
                </button>
                <button class="tb-date-next" @click="offsetPeriod(1)"><i class="fas fa-chevron-right"></i></button>

                <!-- Picker portal -->
                <teleport to="body" v-if="datePickerOpen">
                    <div class="dp-portal" :style="datePickerStyle" @mousedown.stop>

                        <!-- ── MOIS : picker mois/année seulement ── -->
                        <template v-if="viewMode === 'month'">
                            <div class="dp-header">
                                <button class="dp-nav" @click="dpYear--"><i class="fas fa-chevron-left"></i></button>
                                <span class="dp-title">{{ dpYear }}</span>
                                <button class="dp-nav" @click="dpYear++"><i class="fas fa-chevron-right"></i></button>
                            </div>
                            <div class="dp-months">
                                <button v-for="(m, i) in monthNames" :key="i"
                                    class="dp-month-btn"
                                    :class="{ active: dpYear === currentYear && i === currentMonth }"
                                    @click="pickMonth(i)">{{ m }}</button>
                            </div>
                        </template>

                        <!-- ── JOUR / SEMAINE : date picker complet ── -->
                        <template v-else>
                            <div class="dp-header">
                                <button class="dp-nav" @click="dpPrevMonth"><i class="fas fa-chevron-left"></i></button>
                                <div class="dp-header-center">
                                    <select class="dp-select" v-model="dpMonth">
                                        <option v-for="(m, i) in monthNames" :key="i" :value="i">{{ m }}</option>
                                    </select>
                                    <select class="dp-select" v-model="dpYear">
                                        <option v-for="y in dpYearRange" :key="y" :value="y">{{ y }}</option>
                                    </select>
                                </div>
                                <button class="dp-nav" @click="dpNextMonth"><i class="fas fa-chevron-right"></i></button>
                            </div>
                            <div class="dp-weekdays">
                                <span v-for="wd in ['L','M','M','J','V','S','D']" :key="wd+'_'+Math.random()">{{ wd }}</span>
                            </div>
                            <div class="dp-grid">
                                <span v-for="_ in dpFirstDow" :key="'e'+_" class="dp-empty"></span>
                                <button v-for="day in dpDaysInMonth" :key="day"
                                    class="dp-day"
                                    :class="{
                                        today: isPickerToday(day),
                                        selected: isPickerSelected(day),
                                        'in-week': viewMode === 'week' && isDayInSelectedWeek(day),
                                    }"
                                    @click="pickDay(day)">{{ day }}</button>
                            </div>
                            <div class="dp-footer">
                                <button class="dp-today-btn" @click="goToday">Aujourd'hui</button>
                            </div>
                        </template>

                    </div>
                </teleport>
            </div>
        </header>

        <!-- ══ GLOBAL LOADER ══ -->
        <transition name="fade">
            <div v-if="loading" class="global-loader">
                <div class="loader-spinner large"></div>
                <span class="loader-label">Chargement…</span>
            </div>
        </transition>

        <!-- ══════════════════════════════════════════
             VUE JOUR
        ══════════════════════════════════════════ -->
        <div v-if="viewMode === 'day'" class="view-day">
            <div class="day-header-row">
                <div class="day-label-corner"><span class="corner-label">Techniciens</span></div>
                <div class="day-slot-col-head morning-head">
                    <div>
                        <div class="slot-head-name">Matin</div>
                    </div>
                </div>
                <div class="day-slot-col-head afternoon-head">
                    <div>
                        <div class="slot-head-name">Après-midi</div>
                    </div>
                </div>
            </div>

            <div class="day-body">
                <div v-for="tech in filteredTechnicians" :key="'dr-'+tech.id" class="day-tech-row">
                    <div class="day-row-label" :style="{ borderLeftColor: tech.color || '#3b82f6' }">
                        <div class="tech-avatar" :style="{ background: tech.color || '#3b82f6' }">{{ initials(tech.name) }}</div>
                        <div class="tech-name-wrap">
                            <div class="tech-name">{{ tech.name }}</div>
                            <div class="tech-role" v-if="tech.role">{{ tech.role }}</div>
                        </div>
                    </div>

                    <!-- ── Matin ── -->
                    <div class="day-cell morning-cell"
                        :class="[cellStateClass(tech.id, selectedDate, 'morning'), { 'ms-selected-cell': multiSlots && isSlotSelected(tech.id, selectedDate, 'morning') }]">
                        <div class="cell-saturation-bar">
                            <div class="cell-sat-fill" :style="satBarStyle(tech.id, selectedDate, 'morning')"></div>
                        </div>
                        <!-- Overlay sélection multi-slot -->
                        <div v-if="multiSlots && isSlotSelected(tech.id, selectedDate, 'morning')" class="ms-slot-overlay">
                            <div class="ms-check-badge"><i class="fas fa-check"></i></div>
                            <span class="ms-time-remaining">{{ formatDuration(slotRemainingMins(tech.id, selectedDate, 'morning')) }} dispo</span>
                        </div>
                        <div class="cell-content">
                            <div v-for="ev in getEventsForCell(tech.id, selectedDate, 'morning')" :key="'dm-'+ev.id"
                                class="cell-event" :class="[evTypeClass(ev), { 'ev-preview': ev._isPreview }]"
                                :style="cellEventStyle(ev, tech)"
                                @click.stop="$emit('event-click', ev)">
                                <div class="cev-stripe" :style="{ background: ev._isPreview ? '#f59e0b' : (ev.color || tech.color || '#3b82f6') }"></div>
                                <div class="cev-body">
                                    <div class="cev-title">{{ ev.title }}</div>
                                    <div class="cev-location" v-if="ev.ville"><i class="fas fa-map-marker-alt cev-sub-icon"></i>{{ ev.ville }}</div>
                                    <div class="cev-duration" v-if="ev.estimatedDuration || ev.duration"><i class="far fa-clock cev-sub-icon"></i>{{ formatDuration(ev.estimatedDuration || ev.duration) }}</div>
                                    <div v-if="ev._isPreview" class="preview-chip"><i class="fas fa-eye"></i> Aperçu</div>
                                </div>
                                <div v-if="ev.status" class="cev-status" :class="'s-'+ev.status"></div>
                            </div>
                            <div v-if="isOverloaded(tech.id, selectedDate, 'morning')" class="overload-badge">
                                <i class="fas fa-exclamation-triangle"></i> Surcharge
                            </div>
                        </div>
                        <!-- Bouton normal -->
                        <button v-if="!readonly && !multiSlots" class="cell-add-btn"
                            @click.stop="$emit('new-event', { techId: tech.id, date: selectedDate, slot: 'morning' })">
                            <i class="fas fa-plus"></i>
                        </button>
                        <!-- Bouton multi-slot -->
                        <button v-if="multiSlots" class="ms-toggle-btn" :class="{ 'ms-toggle-active': isSlotSelected(tech.id, selectedDate, 'morning') }"
                            @click.stop="toggleSlot(tech.id, selectedDate, 'morning')">
                            <i :class="isSlotSelected(tech.id, selectedDate, 'morning') ? 'fas fa-check-circle' : 'fas fa-plus-circle'"></i>
                        </button>
                    </div>

                    <!-- ── Après-midi ── -->
                    <div class="day-cell afternoon-cell"
                        :class="[cellStateClass(tech.id, selectedDate, 'afternoon'), { 'ms-selected-cell': multiSlots && isSlotSelected(tech.id, selectedDate, 'afternoon') }]">
                        <div class="cell-saturation-bar">
                            <div class="cell-sat-fill" :style="satBarStyle(tech.id, selectedDate, 'afternoon')"></div>
                        </div>
                        <div v-if="multiSlots && isSlotSelected(tech.id, selectedDate, 'afternoon')" class="ms-slot-overlay">
                            <div class="ms-check-badge"><i class="fas fa-check"></i></div>
                            <span class="ms-time-remaining">{{ formatDuration(slotRemainingMins(tech.id, selectedDate, 'afternoon')) }} dispo</span>
                        </div>
                        <div class="cell-content">
                            <div v-for="ev in getEventsForCell(tech.id, selectedDate, 'afternoon')" :key="'da-'+ev.id"
                                class="cell-event" :class="[evTypeClass(ev), { 'ev-preview': ev._isPreview }]"
                                :style="cellEventStyle(ev, tech)"
                                @click.stop="$emit('event-click', ev)">
                                <div class="cev-stripe" :style="{ background: ev._isPreview ? '#f59e0b' : (ev.color || tech.color || '#3b82f6') }"></div>
                                <div class="cev-body">
                                    <div class="cev-title">{{ ev.title }}</div>
                                    <div class="cev-location" v-if="ev.ville"><i class="fas fa-map-marker-alt cev-sub-icon"></i>{{ ev.ville }}</div>
                                    <div class="cev-duration" v-if="ev.estimatedDuration || ev.duration"><i class="far fa-clock cev-sub-icon"></i>{{ formatDuration(ev.estimatedDuration || ev.duration) }}</div>
                                    <div v-if="ev._isPreview" class="preview-chip"><i class="fas fa-eye"></i> Aperçu</div>
                                </div>
                                <div v-if="ev.status" class="cev-status" :class="'s-'+ev.status"></div>
                            </div>
                            <div v-if="isOverloaded(tech.id, selectedDate, 'afternoon')" class="overload-badge">
                                <i class="fas fa-exclamation-triangle"></i> Surcharge
                            </div>
                        </div>
                        <button v-if="!readonly && !multiSlots" class="cell-add-btn"
                            @click.stop="$emit('new-event', { techId: tech.id, date: selectedDate, slot: 'afternoon' })">
                            <i class="fas fa-plus"></i>
                        </button>
                        <button v-if="multiSlots" class="ms-toggle-btn" :class="{ 'ms-toggle-active': isSlotSelected(tech.id, selectedDate, 'afternoon') }"
                            @click.stop="toggleSlot(tech.id, selectedDate, 'afternoon')">
                            <i :class="isSlotSelected(tech.id, selectedDate, 'afternoon') ? 'fas fa-check-circle' : 'fas fa-plus-circle'"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- ══════════════════════════════════════════
             VUE SEMAINE
        ══════════════════════════════════════════ -->
        <div v-if="viewMode === 'week'" class="view-week">
            <div class="week-scroll" ref="weekScroll" @mousedown="onPanStart" :class="{ 'is-panning': isPanning }">
                <div class="week-inner">
                    <div class="wk-header-row">
                        <div class="wk-corner"><span class="corner-label">Techniciens</span></div>
                        <div v-for="day in weekDays" :key="'wkh-'+day.date"
                            class="wk-day-head"
                            :class="{ 'wkh-today': isTodayDate(day.date), 'wkh-weekend': day.isWeekend }"
                            @click="!isPanning && (selectDate(day.date), viewMode='day')">
                            <div class="wkh-name">{{ day.shortName }}</div>
                            <div class="wkh-num" :class="{ 'wkh-today-badge': isTodayDate(day.date) }">{{ day.num }}</div>
                            <div class="wkh-slot-labels">
                                <span class="wkh-slot-lbl morning-lbl">M</span>
                                <span class="wkh-slot-lbl afternoon-lbl">AP</span>
                            </div>
                        </div>
                    </div>

                    <div v-for="tech in filteredTechnicians" :key="'wkr-'+tech.id" class="wk-tech-row">
                        <div class="wk-tech-label" :style="{ borderLeftColor: tech.color || '#3b82f6' }">
                            <div class="tech-avatar sm" :style="{ background: tech.color || '#3b82f6' }">{{ initials(tech.name) }}</div>
                            <div class="tech-name-wrap">
                                <div class="tech-name">{{ tech.name }}</div>
                            </div>
                        </div>
                        <div v-for="day in weekDays" :key="'wkc-'+tech.id+'-'+day.date"
                            class="wk-day-cell"
                            :class="{ 'wkc-today': isTodayDate(day.date), 'wkc-weekend': day.isWeekend, 'wkc-multiSlots': multiSlots }"
                            @click="!isPanning && !multiSlots && (selectDate(day.date), viewMode='day')">

                            <!-- Demi-journées -->
                            <div class="wk-halves">

                                <!-- Matin -->
                                <div class="wk-half morning-half"
                                    :class="[cellStateClass(tech.id, day.date, 'morning'), { 'ms-selected-half': multiSlots && isSlotSelected(tech.id, day.date, 'morning') }]"
                                    @click.stop="!isPanning && (multiSlots ? toggleSlot(tech.id, day.date, 'morning') : !readonly && $emit('new-event', { techId: tech.id, date: day.date, slot: 'morning' }))">
                                    <div class="wk-sat-bar">
                                        <div class="wk-sat-fill" :style="satBarStyle(tech.id, day.date, 'morning')"></div>
                                    </div>
                                    <div v-if="multiSlots && isSlotSelected(tech.id, day.date, 'morning')" class="ms-wk-overlay">
                                        <div class="ms-wk-check"><i class="fas fa-check"></i></div>
                                    </div>
                                    <div class="wk-chips">
                                        <div v-for="ev in getEventsForCell(tech.id, day.date, 'morning')" :key="'wkm-'+ev.id"
                                            class="wk-chip" :class="{ 'ev-preview': ev._isPreview }"
                                            :style="wkChipStyle(ev, tech)"
                                            @click.stop="!isPanning && $emit('event-click', ev)">
                                            <span class="wk-chip-title">{{ ev.title }}</span>
                                        </div>
                                    </div>
                                    <div v-if="isOverloaded(tech.id, day.date, 'morning')" class="wk-overload-flag">
                                        <i class="fas fa-exclamation-triangle"></i>
                                    </div>
                                    <button v-if="!readonly" class="wk-add-btn"
                                        @click.stop="!isPanning && (multiSlots ? toggleSlot(tech.id, day.date, 'morning') : $emit('new-event', { techId: tech.id, date: day.date, slot: 'morning' }))">
                                        <i class="fas fa-plus"></i>
                                    </button>
                                </div>

                                <!-- Après-midi -->
                                <div class="wk-half afternoon-half"
                                    :class="[cellStateClass(tech.id, day.date, 'afternoon'), { 'ms-selected-half': multiSlots && isSlotSelected(tech.id, day.date, 'afternoon') }]"
                                    @click.stop="!isPanning && (multiSlots ? toggleSlot(tech.id, day.date, 'afternoon') : !readonly && $emit('new-event', { techId: tech.id, date: day.date, slot: 'afternoon' }))">
                                    <div class="wk-sat-bar">
                                        <div class="wk-sat-fill" :style="satBarStyle(tech.id, day.date, 'afternoon')"></div>
                                    </div>
                                    <div v-if="multiSlots && isSlotSelected(tech.id, day.date, 'afternoon')" class="ms-wk-overlay">
                                        <div class="ms-wk-check"><i class="fas fa-check"></i></div>
                                    </div>
                                    <div class="wk-chips">
                                        <div v-for="ev in getEventsForCell(tech.id, day.date, 'afternoon')" :key="'wka-'+ev.id"
                                            class="wk-chip" :class="{ 'ev-preview': ev._isPreview }"
                                            :style="wkChipStyle(ev, tech)"
                                            @click.stop="!isPanning && $emit('event-click', ev)">
                                            <span class="wk-chip-title">{{ ev.title }}</span>
                                        </div>
                                    </div>
                                    <div v-if="isOverloaded(tech.id, day.date, 'afternoon')" class="wk-overload-flag">
                                        <i class="fas fa-exclamation-triangle"></i>
                                    </div>
                                    <button v-if="!readonly" class="wk-add-btn"
                                        @click.stop="!isPanning && (multiSlots ? toggleSlot(tech.id, day.date, 'afternoon') : $emit('new-event', { techId: tech.id, date: day.date, slot: 'afternoon' }))">
                                        <i class="fas fa-plus"></i>
                                    </button>
                                </div>

                            </div>

                            <!-- Bouton journée entière (multiSlots uniquement) -->
                            <button v-if="multiSlots && !readonly" class="wk-day-select-btn"
                                :class="{ 'wk-day-select-all': isSlotSelected(tech.id, day.date, 'morning') && isSlotSelected(tech.id, day.date, 'afternoon') }"
                                @click.stop="!isPanning && toggleFullDay(tech.id, day.date)">
                                <i class="fas fa-plus"></i>
                                <span>Journée</span>
                            </button>

                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ══════════════════════════════════════════
             VUE MOIS
        ══════════════════════════════════════════ -->
        <div v-if="viewMode === 'month'" class="view-month">
            <div class="month-weekdays" :style="{ gridTemplateColumns: 'repeat(' + monthColCount + ', 1fr)' }">
                <div v-for="wd in monthWeekdayLabels" :key="wd" class="month-wd">{{ wd }}</div>
            </div>
            <div class="month-grid" :style="{ gridTemplateColumns: 'repeat(' + monthColCount + ', 1fr)' }">
                <div v-for="(cell, i) in monthCells" :key="'mc-'+i"
                    class="month-cell"
                    :class="{ 'mc-empty': !cell, 'mc-today': cell && isTodayDate(cell.date), 'mc-selected': cell && cell.date === selectedDate, 'mc-weekend': cell && cell.isWeekend, 'mc-has-overload': cell && hasCellOverload(cell.date) }"
                    @click="cell && selectDate(cell.date); cell && (viewMode = 'day')">
                    <template v-if="cell">
                        <div class="mc-num" :class="{ 'mc-today-badge': isTodayDate(cell.date) }">{{ cell.day }}</div>
                        <div class="mc-tech-rows">
                            <div v-for="tech in filteredTechnicians.slice(0, 4)" :key="'mctr-'+tech.id" class="mc-tech-row">
                                <div class="mc-tech-dot" :style="{ background: tech.color || '#3b82f6' }"></div>
                                <div class="mc-slot-pips">
                                    <div v-for="slot in halfDaySlots" :key="'mcsp-'+slot.id"
                                        class="mc-slot-pip"
                                        :class="{
                                            'pip-busy': getEventsForCell(tech.id, cell.date, slot.id).length > 0,
                                            'pip-overload': isOverloaded(tech.id, cell.date, slot.id),
                                            'pip-preview': hasCellPreviewForTech(cell.date, tech.id, slot.id),
                                            'pip-ms-selected': multiSlots && isSlotSelected(tech.id, cell.date, slot.id)
                                        }"
                                        :style="!isOverloaded(tech.id, cell.date, slot.id) && getEventsForCell(tech.id, cell.date, slot.id).length > 0
                                            ? { background: hasCellPreviewForTech(cell.date, tech.id, slot.id) ? '#f59e0b' : tech.color || '#3b82f6' }
                                            : {}">
                                    </div>
                                </div>
                            </div>
                            <div v-if="filteredTechnicians.length > 4" class="mc-more">+{{ filteredTechnicians.length - 4 }}</div>
                        </div>
                        <div v-if="hasCellOverload(cell.date)" class="mc-overload-flag"><i class="fas fa-exclamation-triangle"></i></div>
                        <div v-if="hasCellPreview(cell.date)" class="mc-preview-star"><i class="fas fa-eye"></i></div>
                    </template>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
const MOBILE_BREAKPOINT = 768

export default {
    name: 'PlanningHalfDay',

    props: {
        heureDebutMatin:     { type: String,  default: '08:00' },
        heurefinMatin:       { type: String,  default: '12:00' },
        heureDebutApresMidi: { type: String,  default: '13:00' },
        heureFinApresMidi:   { type: String,  default: '17:00' },
        previewIntervention: { type: Object,  default: null },
        readonly:            { type: Boolean, default: false },
        // ── NOUVEAU ──
        multiSlots:          { type: Boolean, default: false },
        // Jours à afficher ex: ['lundi','mardi','jeudi'] — null = tous
        days:                { type: Array,   default: null },
        chantierId:          { type: [String, Number], default: null },
    },

    data() {
        const today = new Date()
        const pad   = n => String(n).padStart(2, '0')
        return {
            isMobile:       false,
            technicians:    [],
            selectedDate:   `${today.getFullYear()}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`,
            viewMode:       'week',
            filterTechId:   null,
            planningEvents: [],
            loading:        false,
            isPanning:      false,
            panStartX:      0,
            panScrollLeft:  0,
            selectOpen:    false,
            dropdownStyle: {},
            _leaveTimer: null,

            datePickerOpen: false,
            datePickerStyle: {},
            dpMonth: new Date().getMonth(),
            dpYear: new Date().getFullYear(),
            monthNames: ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'],

            // ── Multi-slot : tableau de clés "techId|date|slotId" ──
            selectedSlotKeys: [],
        }
    },

    computed: {
        halfDaySlots() {
            return [
                { id: 'morning',   label: 'Matin',      durationMins: this.timeDiffMins(this.heureDebutMatin, this.heurefinMatin) },
                { id: 'afternoon', label: 'Après-midi', durationMins: this.timeDiffMins(this.heureDebutApresMidi, this.heureFinApresMidi) },
            ]
        },
        filteredTechnicians() {
            return this.filterTechId ? this.technicians.filter(t => t.id === this.filterTechId) : this.technicians
        },
        isToday() { return this.selectedDate === this.todayStr() },
        weekDays() {
            const DAY_NAMES = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi']
            const start = this.getWeekStart(this.selectedDate)
            const all = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(start); d.setDate(d.getDate() + i)
                const pad = n => String(n).padStart(2, '0')
                return {
                    date: `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`,
                    num: d.getDate(),
                    shortName: d.toLocaleDateString('fr-FR', { weekday: 'short' }),
                    isWeekend: d.getDay() === 0 || d.getDay() === 6,
                    dayName: DAY_NAMES[d.getDay()],
                }
            })
            if (!this.days || this.days.length === 0) return all
            const filter = this.days.map(d => d.toLowerCase())
            return all.filter(d => filter.includes(d.dayName))
        },
        activeDayIndices() {
            if (!this.days || this.days.length === 0) return null
            const DAY_NAMES = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi']
            return this.days.map(d => DAY_NAMES.indexOf(d.toLowerCase())).filter(i => i !== -1)
        },
        monthCells() {
            const d = new Date(this.selectedDate + 'T00:00:00')
            const year = d.getFullYear(), month = d.getMonth()
            const pad = n => String(n).padStart(2, '0')
            const dim = new Date(year, month + 1, 0).getDate()

            if (this.activeDayIndices) {
                // Mode jours filtrés : pas de grille 7 colonnes, on affiche juste les jours actifs
                const cells = []
                for (let day = 1; day <= dim; day++) {
                    const date = `${year}-${pad(month+1)}-${pad(day)}`
                    const dow = new Date(date + 'T00:00:00').getDay()
                    if (this.activeDayIndices.includes(dow)) {
                        cells.push({ day, date, isWeekend: [0, 6].includes(dow) })
                    }
                }
                return cells
            }

            const startDow = (new Date(year, month, 1).getDay() + 6) % 7
            const cells = Array(startDow).fill(null)
            for (let day = 1; day <= dim; day++) {
                const date = `${year}-${pad(month+1)}-${pad(day)}`
                cells.push({ day, date, isWeekend: [0, 6].includes(new Date(date + 'T00:00:00').getDay()) })
            }
            return cells
        },
        overloadWarnings() {
            const dates = this.viewMode === 'day' ? [this.selectedDate]
                : this.viewMode === 'week' ? this.weekDays.map(d => d.date)
                : this.monthCells.filter(Boolean).map(c => c.date)
            const warnings = []
            for (const date of dates) {
                for (const tech of this.filteredTechnicians) {
                    for (const slot of this.halfDaySlots) {
                        if (this.isOverloaded(tech.id, date, slot.id)) {
                            const dl = this.viewMode !== 'day' ? new Date(date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : null
                            warnings.push({ techName: tech.name.split(' ')[0], slotLabel: slot.label, dateLabel: dl })
                        }
                    }
                }
            }
            return warnings.slice(0, 6)
        },
        selectedTech() {
            return this.technicians.find(t => t.id === this.filterTechId) || null
        },
        dateRangeLabel() {
            const d = new Date(this.selectedDate + 'T00:00:00')
            if (this.viewMode === 'day') {
                return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
            }
            if (this.viewMode === 'week') {
                const days = this.weekDays
                if (!days || days.length === 0) return ''
                const start = new Date(days[0].date + 'T00:00:00')
                const end   = new Date(days[days.length - 1].date + 'T00:00:00')
                const fmt = { day: 'numeric', month: 'short' }
                return start.toLocaleDateString('fr-FR', fmt) + ' – ' + end.toLocaleDateString('fr-FR', { ...fmt, year: 'numeric' })
            }
            return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
        },
        currentYear()  { return new Date(this.selectedDate + 'T00:00:00').getFullYear() },
        currentMonth() { return new Date(this.selectedDate + 'T00:00:00').getMonth() },
        dpYearRange() {
            const y = new Date().getFullYear()
            return Array.from({ length: 12 }, (_, i) => y - 3 + i)
        },
        dpFirstDow() {
            return (new Date(this.dpYear, this.dpMonth, 1).getDay() + 6) % 7
        },
        dpDaysInMonth() {
            return new Date(this.dpYear, this.dpMonth + 1, 0).getDate()
        },

        monthColCount() {
            return this.activeDayIndices ? this.activeDayIndices.length : 7
        },
        monthWeekdayLabels() {
            const ALL = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim']
            if (!this.days || this.days.length === 0) return ALL
            const DAY_NAMES = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi']
            // ordre lun=1..dim=0, on ramène à index 0=lun..6=dim
            return this.days
                .map(d => DAY_NAMES.indexOf(d.toLowerCase()))
                .filter(i => i !== -1)
                .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
                .map(i => ALL[(i + 6) % 7])
        },
        // ── Slots sélectionnés (exposé au parent via l'event) ──
        selectedSlots() {
            return this.selectedSlotKeys.map(key => {
                const [techId, date, slotId] = key.split('|')
                const tech = this.technicians.find(t => String(t.id) === String(techId))
                return {
                    date,
                    account_id: techId,
                    slot:       slotId,
                    time:       this.slotRemainingMins(techId, date, slotId),
                    techName:   tech ? tech.name : techId,
                }
            })
        },
    },

    watch: {
        selectedDate() { this.fetchEvents() },
        viewMode()     { this.fetchEvents() },
        selectedSlots(val) {
            this.$emit('slot-selection-change', val)
        },
    },

    methods: {

        // ── Multi-slot ────────────────────────────────────────────────
        slotKey(techId, date, slotId) {
            return `${techId}|${date}|${slotId}`
        },
        isSlotSelected(techId, date, slotId) {
            return this.selectedSlotKeys.indexOf(this.slotKey(techId, date, slotId)) !== -1
        },
        toggleSlot(techId, date, slotId) {
            if (!this.multiSlots) return
            const key = this.slotKey(techId, date, slotId)
            const idx = this.selectedSlotKeys.indexOf(key)
            if (idx === -1) {
                this.selectedSlotKeys.push(key)
            } else {
                this.selectedSlotKeys.splice(idx, 1)
            }
        },
        clearSelection() {
            this.selectedSlotKeys = []
        },
        toggleFullDay(techId, date) {
            var bothSelected = this.isSlotSelected(techId, date, 'morning') && this.isSlotSelected(techId, date, 'afternoon')
            var self = this
            if (bothSelected) {
                ;['morning', 'afternoon'].forEach(function(slot) {
                    var idx = self.selectedSlotKeys.indexOf(self.slotKey(techId, date, slot))
                    if (idx !== -1) self.selectedSlotKeys.splice(idx, 1)
                })
            } else {
                ;['morning', 'afternoon'].forEach(function(slot) {
                    var key = self.slotKey(techId, date, slot)
                    if (self.selectedSlotKeys.indexOf(key) === -1) self.selectedSlotKeys.push(key)
                })
            }
        },
        slotRemainingMins(techId, date, slotId) {
            const cap  = this.slotCapacityMins(slotId)
            const used = this.cellTotalMins(techId, date, slotId)
            const exact = Math.max(0, cap - used)
            // Arrondi au multiple de 30 le plus proche
            return Math.round(exact / 30) * 30
        },

        // ── Utilitaires ───────────────────────────────────────────────
        todayStr() {
            const t = new Date(), pad = n => String(n).padStart(2, '0')
            return `${t.getFullYear()}-${pad(t.getMonth()+1)}-${pad(t.getDate())}`
        },
        pad(n) { return String(n).padStart(2, '0') },
        isTodayDate(s) { return s === this.todayStr() },
        toDateStr(d) { return `${d.getFullYear()}-${this.pad(d.getMonth()+1)}-${this.pad(d.getDate())}` },
        getWeekStart(dateStr) {
            const d = new Date(dateStr + 'T00:00:00')
            d.setDate(d.getDate() - (d.getDay() + 6) % 7)
            return d
        },
        timeDiffMins(start, end) {
            const [sh, sm] = start.split(':').map(Number), [eh, em] = end.split(':').map(Number)
            return (eh * 60 + em) - (sh * 60 + sm)
        },
        selectDate(dateStr) {
            if (!dateStr) return
            this.selectedDate = dateStr
            this.$emit('date-change', dateStr)
        },
        offsetPeriod(delta) {
            const d = new Date(this.selectedDate + 'T00:00:00')
            if (this.viewMode === 'day')   d.setDate(d.getDate() + delta)
            if (this.viewMode === 'week')  d.setDate(d.getDate() + delta * 7)
            if (this.viewMode === 'month') d.setMonth(d.getMonth() + delta)
            this.selectDate(this.toDateStr(d))
        },

        // ── Pan ───────────────────────────────────────────────────────
        onPanStart(e) {
            if (e.button !== 0) return
            const el = this.$refs.weekScroll
            if (!el) return
            e.preventDefault()
            this.isPanning    = false
            this.panStartX    = e.clientX
            this.panScrollLeft = el.scrollLeft
            document.addEventListener('mousemove', this._onPanMove)
            document.addEventListener('mouseup',   this._onPanEnd)
        },
        _onPanMove(e) {
            const dx = e.clientX - this.panStartX
            if (!this.isPanning && Math.abs(dx) > 5) this.isPanning = true
            if (this.isPanning) {
                const el = this.$refs.weekScroll
                if (el) el.scrollLeft = this.panScrollLeft - dx
            }
        },
        _onPanEnd() {
            document.removeEventListener('mousemove', this._onPanMove)
            document.removeEventListener('mouseup',   this._onPanEnd)
            setTimeout(() => { this.isPanning = false }, 50)
        },

        // ── API ───────────────────────────────────────────────────────
        getDateRange() {
            if (this.viewMode === 'day') return { start: this.selectedDate, end: this.selectedDate }
            if (this.viewMode === 'week') {
                const s = this.getWeekStart(this.selectedDate), e = new Date(s); e.setDate(e.getDate() + 6)
                return { start: this.toDateStr(s), end: this.toDateStr(e) }
            }
            const d = new Date(this.selectedDate + 'T00:00:00'), year = d.getFullYear(), month = d.getMonth()
            return { start: this.toDateStr(new Date(year, month, 1)), end: this.toDateStr(new Date(year, month + 1, 0)) }
        },
        async fetchEvents() {
            this.loading = true
            try {
                const { start, end } = this.getDateRange()
                const response = await this.$request.post('/interventions/on-period', {
                    date_debut: start,
                    date_fin: end,
                    chantier_id: this.chantierId || 0
                })

                this.planningEvents = response.map(e => ({
                    id:                e.id,
                    technicianId:      e.technicien_id,
                    date:              e.date,
                    slot:              e.moment_day || null,
                    title:             (e.intervention?.client?.nom ? (e.intervention?.client?.nom + ' ' + e.intervention?.client?.prenom) : '') || e.type || e.titre || '—',
                    type:              e.type || 'intervention',
                    ville:             e.intervention?.adresse?.ville || null,
                    color:             e.color || null,
                    status:            e.statut || e.intervention?.statut?.libelle || 'planned',
                    estimatedDuration: e.intervention?.duree_estimee || e.duree || null,
                    interventionId:    e.intervention?.id ?? null,
                    chantierId:      e.chantier_id ?? null,
                }))
                this.$emit('period-change', { start, end, type: this.viewMode })
            } catch (err) {
                console.error('Erreur récupération planning :', err)
            } finally {
                this.loading = false
            }
        },
        slotFromTime(h) {
            if (!h) return 'morning'
            return parseInt(h.split(':')[0]) < 12 ? 'morning' : 'afternoon'
        },
        async getTechniciens() {
            try {
                const response = await this.$request.get('/techniciens')
                this.technicians = response.map(t => ({
                    id:    t.id,
                    name:  t.prenom + ' ' + t.nom,
                    role:  'Technicien',
                    color: t.color || '#3b82f6',
                }))
            } catch (e) {
                console.error('Erreur récupération techniciens :', e)
            }
        },

        // ── Events ────────────────────────────────────────────────────
        getEventsForCell(techId, date, slotId) {
            const evs = this.planningEvents.filter(ev =>
                ev.slot !== null &&
                ev.technicianId === techId && ev.date === date && (ev.slot === slotId || ev.slot === 'full_day')
            )
            if (this.previewIntervention) {
                const p = this.previewIntervention
                if ((!p.date || p.date === date) && (!p.technicianId || p.technicianId === techId) && (!p.slot || p.slot === slotId || p.slot === 'full_day'))
                    evs.push({ ...p, _isPreview: true })
            }
            return evs
        },
        isCellEmpty(techId, date, slotId) {
            return this.getEventsForCell(techId, date, slotId).filter(e => !e._isPreview).length === 0
        },
        cellTotalMins(techId, date, slotId) {
            return this.getEventsForCell(techId, date, slotId).filter(e => !e._isPreview).reduce((s, e) => {
                const duration = parseInt(e.estimatedDuration) || 0
                return s + duration
            }, 0)
        },
        slotCapacityMins(slotId) { return (this.halfDaySlots.find(s => s.id === slotId) || {}).durationMins || 240 },
        isOverloaded(techId, date, slotId) { return this.cellTotalMins(techId, date, slotId) > this.slotCapacityMins(slotId) },
        techSlotCapacityPercent(techId, date, slotId) {
            const cap = this.slotCapacityMins(slotId)
            if (!cap) return 0
            return Math.min(100, Math.round(this.cellTotalMins(techId, date, slotId) / cap * 100))
        },
        techSlotCapacityColor(techId, date, slotId) {
            const p = this.techSlotCapacityPercent(techId, date, slotId)
            if (p >= 100 || this.isOverloaded(techId, date, slotId)) return '#ef4444'
            if (p >= 60)  return '#f59e0b'
            if (p > 0)    return '#22c55e'
            return 'transparent'
        },
        satBarStyle(techId, date, slotId) {
            const isOver = this.isOverloaded(techId, date, slotId)
            const p = this.techSlotCapacityPercent(techId, date, slotId)
            const color = this.techSlotCapacityColor(techId, date, slotId)
            return { width: (isOver ? 100 : p) + '%', background: color }
        },
        cellStateClass(techId, date, slotId) {
            if (this.isOverloaded(techId, date, slotId)) return 'cell-overload'
            if (!this.isCellEmpty(techId, date, slotId)) return 'cell-busy'
            return 'cell-free'
        },
        hasBusyDay(techId, date) { return this.halfDaySlots.some(s => this.getEventsForCell(techId, date, s.id).length > 0) },
        hasCellOverload(date) { return this.filteredTechnicians.some(t => this.halfDaySlots.some(s => this.isOverloaded(t.id, date, s.id))) },
        hasCellPreview(date) { return !!this.previewIntervention && (!this.previewIntervention.date || this.previewIntervention.date === date) },
        hasCellPreviewForTech(date, techId, slotId) {
            if (!this.previewIntervention) return false
            const p = this.previewIntervention
            return (!p.date || p.date === date) && (!p.technicianId || p.technicianId === techId) && (!p.slot || p.slot === slotId || p.slot === 'full_day')
        },

        // ── Affichage ─────────────────────────────────────────────────
        initials(name) { return (name || '').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) },
        formatDuration(mins) {
            if (!mins) return ''
            const h = Math.floor(mins / 60), m = mins % 60
            return h ? (m ? `${h}h${String(m).padStart(2,'0')}` : `${h}h`) : `${m}min`
        },
        cellEventStyle(ev, tech) {
            const c = ev._isPreview ? '#f59e0b' : (ev.color || tech.color || '#3b82f6')
            return { '--ev-color': c, background: c + '14', borderColor: c + '55' }
        },
        wkChipStyle(ev, tech) {
            const c = ev._isPreview ? '#f59e0b' : (ev.color || tech.color || '#3b82f6')
            return { background: c + '18', borderLeftColor: c }
        },
        evTypeClass(ev) { return { 'ev-absence': ev.type === 'absence' || ev.type === 'conge', 'ev-deplacement': ev.type === 'deplacement' } },
        checkMobile() { this.isMobile = window.innerWidth < MOBILE_BREAKPOINT },
        onEnter() {
            clearTimeout(this._leaveTimer)
            this.selectOpen = true
            this.$nextTick(() => this.updateDropdownPos())
        },
        onLeave() {
            this._leaveTimer = setTimeout(() => { this.selectOpen = false }, 120)
        },
        updateDropdownPos() {
            const trigger = this.$refs.csTrigger
            if (!trigger) return
            const rect = trigger.getBoundingClientRect()
            this.dropdownStyle = {
                position:  'fixed',
                top:       rect.bottom + 4 + 'px',
                left:      rect.left + 'px',
                minWidth:  rect.width + 'px',
                zIndex:    99999,
            }
        },
        toggleDatePicker() {
            this.datePickerOpen = !this.datePickerOpen
            if (this.datePickerOpen) {
                const d = new Date(this.selectedDate + 'T00:00:00')
                this.dpMonth = d.getMonth()
                this.dpYear  = d.getFullYear()
                this.$nextTick(() => this.updateDatePickerPos())
            }
        },
        updateDatePickerPos() {
            const trigger = this.$el.querySelector('.tb-date-trigger')
            if (!trigger) return
            const rect = trigger.getBoundingClientRect()
            this.datePickerStyle = {
                position: 'fixed',
                top:  rect.bottom + 4 + 'px',
                left: rect.left + 'px',
                zIndex: 99999,
            }
        },
        closeDatePicker() { this.datePickerOpen = false },
        dpPrevMonth() { if (this.dpMonth === 0) { this.dpMonth = 11; this.dpYear-- } else this.dpMonth-- },
        dpNextMonth() { if (this.dpMonth === 11) { this.dpMonth = 0; this.dpYear++ } else this.dpMonth++ },
        isPickerToday(day) {
            const t = new Date()
            return day === t.getDate() && this.dpMonth === t.getMonth() && this.dpYear === t.getFullYear()
        },
        isPickerSelected(day) {
            const s = new Date(this.selectedDate + 'T00:00:00')
            return day === s.getDate() && this.dpMonth === s.getMonth() && this.dpYear === s.getFullYear()
        },
        isDayInSelectedWeek(day) {
            const cell = new Date(this.dpYear, this.dpMonth, day)
            const ws = this.getWeekStart(this.selectedDate)
            const we = new Date(ws); we.setDate(we.getDate() + 6)
            return cell >= ws && cell <= we
        },
        pickDay(day) {
            const pad = n => String(n).padStart(2, '0')
            const date = `${this.dpYear}-${pad(this.dpMonth + 1)}-${pad(day)}`
            this.selectDate(date)
            this.$emit('date-change', date)
            this.datePickerOpen = false
        },
        pickMonth(monthIndex) {
            const pad = n => String(n).padStart(2, '0')
            const date = `${this.dpYear}-${pad(monthIndex + 1)}-01`
            this.selectDate(date)
            this.$emit('date-change', date)
            this.datePickerOpen = false
        },
        goToday() {
            const date = this.todayStr()
            this.selectDate(date)
            this.$emit('date-change', date)
            this.datePickerOpen = false
        },
        _onClickOutside(e) {
            if (this.datePickerOpen && !e.target.closest('.dp-portal') && !e.target.closest('.tb-date-nav')) {
                this.datePickerOpen = false
            }
        },
    },

    async mounted() {
        this.checkMobile()
        this._resizeListener = () => this.checkMobile()
        window.addEventListener('resize', this._resizeListener)
        await this.getTechniciens()
        this.fetchEvents()
        document.addEventListener('mousedown', this._onClickOutside)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this._resizeListener)
        document.removeEventListener('mousemove', this._onPanMove)
        document.removeEventListener('mouseup',   this._onPanEnd)
        document.removeEventListener('mousedown', this._onClickOutside)
    },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.planner-root { font-family: 'Figtree', sans-serif; display: flex; flex-direction: column; background: #f0f2f5; color: #1a1d27; overflow: hidden; user-select: none; position: relative; }

/* ══ TOP BAR ══ */
.top-bar { display: flex; align-items: center; gap: 10px; padding: 0 14px; height: 48px; background: #fff; border-bottom: 1px solid #e2e6ed; flex-shrink: 0; z-index: 20; box-shadow: 0 1px 6px #00000008; }
.tb-view-switcher { display: flex; align-items: center; gap: 2px; background: #f0f2f5; border-radius: 9px; padding: 3px; border: 1px solid #e2e6ed; flex-shrink: 0; }
.tb-view-btn { display: flex; align-items: center; gap: 5px; padding: 5px 12px; border-radius: 7px; border: none; background: transparent; cursor: pointer; font-family: 'Figtree', sans-serif; font-size: 12px; font-weight: 500; color: #64748b; transition: all 0.15s; white-space: nowrap; }
.tb-view-btn.active { background: #fff; color: #1a1d27; font-weight: 700; box-shadow: 0 1px 4px #0000001a; }
.tb-tech-filter { display: flex; align-items: center; gap: 4px; overflow-x: auto; flex: 1; }
.tb-tech-filter::-webkit-scrollbar { display: none; }

/* ══ MULTI-SLOT BADGE ══ */
.ms-selection-badge {
    display: flex; align-items: center; gap: 6px;
    padding: 5px 10px 5px 8px; border-radius: 9px;
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    color: #fff; flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(79,70,229,0.35);
    font-size: 12px; font-weight: 600;
}
.ms-badge-icon { font-size: 11px; opacity: 0.85; }
.ms-badge-count { background: rgba(255,255,255,0.25); border-radius: 5px; padding: 1px 6px; font-size: 11px; font-weight: 800; }
.ms-badge-label { font-size: 11.5px; }
.ms-badge-clear { width: 18px; height: 18px; border-radius: 50%; border: none; background: rgba(255,255,255,0.2); color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 9px; transition: background 0.1s; margin-left: 2px; }
.ms-badge-clear:hover { background: rgba(255,255,255,0.35); }
.ms-badge-slide-enter-active, .ms-badge-slide-leave-active { transition: all 0.2s ease; }
.ms-badge-slide-enter, .ms-badge-slide-leave-to { opacity: 0; transform: scale(0.85); }

/* ══ LOADER ══ */
.global-loader { position: absolute; inset: 48px 0 0; z-index: 50; background: rgba(255,255,255,0.85); backdrop-filter: blur(3px); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; }
.loader-spinner { width: 22px; height: 22px; border: 2.5px solid #e2e8f0; border-top-color: #3b82f6; border-radius: 50%; animation: spin 0.7s linear infinite; }
.loader-spinner.large { width: 32px; height: 32px; border-width: 3px; }
.loader-label { font-size: 12.5px; font-weight: 500; color: #94a3b8; }
@keyframes spin { to { transform: rotate(360deg); } }
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ══ SHARED ══ */
.corner-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.2px; color: #b0b7c3; }
.tech-avatar { width: 32px; height: 32px; border-radius: 8px; color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tech-avatar.sm { width: 26px; height: 26px; border-radius: 7px; font-size: 9.5px; }
.tech-name-wrap { min-width: 0; flex: 1; }
.tech-name { font-size: 12.5px; font-weight: 600; color: #1a1d27; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tech-role { font-size: 10.5px; color: #9ba3b2; }

/* ══════════════════════════════════════════
   VUE JOUR
══════════════════════════════════════════ */
.view-day { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: #fff; }
.day-header-row { display: grid; grid-template-columns: 190px 1fr 1fr; border-bottom: 1px solid #e2e6ed; flex-shrink: 0; }
.day-label-corner { display: flex; align-items: center; padding: 10px 14px; border-right: 1px solid #e2e6ed; background: #f8f9fb; }
.day-slot-col-head { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-right: 1px solid #e2e6ed; }
.day-slot-col-head:last-child { border-right: none; }
.morning-head   { background: #fffbf0; border-top: 3px solid #f59e0b; }
.afternoon-head { background: #f0f7ff; border-top: 3px solid #3b82f6; }
.slot-head-name { font-size: 13px; font-weight: 700; color: #1a1d27; }

.day-body { flex: 1; overflow-y: auto; }
.day-body::-webkit-scrollbar { width: 4px; }
.day-body::-webkit-scrollbar-thumb { background: #d0d5df; border-radius: 4px; }

.day-tech-row { display: grid; grid-template-columns: 190px 1fr 1fr; border-bottom: 1px solid #edf0f4; min-height: 150px; }
.day-row-label { display: flex; gap: 9px; padding: 10px 12px; border-right: 1px solid #e2e6ed; border-left: 4px solid transparent; background: #fff; }

.day-cell { border-right: 1px solid #edf0f4; display: flex; flex-direction: column; position: relative; overflow: hidden; }
.day-cell:last-child { border-right: none; }
.morning-cell   { background: #fffdf7; }
.afternoon-cell { background: #f8fbff; }
.morning-cell.cell-busy   { background: #fffaed; }
.afternoon-cell.cell-busy { background: #eef6ff; }
.day-cell.cell-overload { background: #fff5f5 !important; }

/* ── Multi-slot sélectionné (vue jour) ── */
.day-cell.ms-selected-cell { background: #eef2ff !important; outline: 2px solid #6366f1; outline-offset: -2px; }

.ms-slot-overlay {
    position: absolute; top: 5px; left: 0; right: 0; bottom: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 5px; z-index: 5; pointer-events: none;
    background: rgba(99,102,241,0.05);
}
.ms-check-badge {
    width: 28px; height: 28px; border-radius: 50%;
    background: #4f46e5; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; box-shadow: 0 2px 8px rgba(79,70,229,0.4);
    animation: ms-pop 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes ms-pop { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.ms-time-remaining { font-size: 10px; font-weight: 700; color: #4f46e5; background: #e0e7ff; padding: 2px 7px; border-radius: 4px; }

/* Bouton toggle multi-slot (vue jour) */
.ms-toggle-btn {
    flex-shrink: 0; margin: 0 10px 8px; padding: 0;
    width: 28px; height: 28px; border-radius: 50%;
    border: none; cursor: pointer; align-self: center;
    display: flex; align-items: center; justify-content: center;
    font-size: 15px; transition: all 0.15s;
    background: #e8edf5; color: #6366f1;
}
.ms-toggle-btn:hover { background: #4f46e5; color: #fff; transform: scale(1.1); box-shadow: 0 2px 8px rgba(79,70,229,0.35); }
.ms-toggle-btn.ms-toggle-active { background: #4f46e5; color: #fff; box-shadow: 0 2px 8px rgba(79,70,229,0.35); }
.ms-toggle-btn.ms-toggle-active:hover { background: #ef4444; }

.cell-saturation-bar { height: 5px; width: 100%; background: #edf0f4; flex-shrink: 0; }
.cell-sat-fill { height: 100%; transition: width 0.4s ease, background 0.3s; min-width: 0; }

.cell-content { flex: 1; display: flex; flex-direction: column; gap: 5px; padding: 8px 10px; }

.cell-add-btn { flex-shrink: 0; margin: 0 10px 8px; padding: 0; width: 28px; height: 28px; border-radius: 50%; border: none; background: #e8edf5; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 12px; transition: all 0.15s; align-self: center; }
.cell-add-btn:hover { background: #3b82f6; color: #fff; transform: scale(1.1); box-shadow: 0 2px 8px rgba(59,130,246,0.35); }

.cell-event { border: 1px solid; border-radius: 7px; overflow: hidden; display: flex; align-items: stretch; cursor: pointer; transition: box-shadow 0.12s, transform 0.1s; animation: ev-appear 0.18s ease; }
@keyframes ev-appear { from { opacity: 0; transform: translateY(2px); } }
.cell-event:hover { box-shadow: 0 3px 12px rgba(0,0,0,0.1); transform: translateY(-1px); }
.cell-event.ev-preview { border-style: dashed !important; animation: preview-blink 1.3s ease-in-out infinite; }
@keyframes preview-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; box-shadow: 0 0 8px 2px rgba(245,158,11,0.3); } }
.cell-event.ev-absence { opacity: 0.7; }
.cev-stripe { width: 4px; flex-shrink: 0; background: var(--ev-color, #3b82f6); }
.cev-body { flex: 1; padding: 5px 8px; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.cev-title { font-size: 12px; font-weight: 600; color: #1a1d27; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cev-location, .cev-duration { font-size: 10.5px; color: #6b7280; display: flex; align-items: center; gap: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cev-duration { font-family: 'JetBrains Mono', monospace; font-size: 9.5px; color: #9ba3b2; }
.cev-sub-icon { font-size: 8.5px; opacity: 0.5; flex-shrink: 0; }
.cev-status { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; align-self: center; margin: 0 6px 0 3px; }
.s-planned     { background: #3b82f6; }
.s-in_progress { background: #f59e0b; box-shadow: 0 0 0 2px #fef3c7; }
.s-done        { background: #22c55e; }
.s-cancelled   { background: #ef4444; }
.preview-chip { display: inline-flex; align-items: center; gap: 3px; background: #fef3c7; color: #92400e; border: 1px solid #fde68a; font-size: 9px; font-weight: 700; padding: 1px 5px; border-radius: 3px; text-transform: uppercase; }
.overload-badge { display: inline-flex; align-items: center; gap: 4px; background: #fef2f2; color: #ef4444; border: 1px solid #fecaca; font-size: 9.5px; font-weight: 700; padding: 2px 7px; border-radius: 4px; }

/* ══════════════════════════════════════════
   VUE SEMAINE
══════════════════════════════════════════ */
.view-week { flex: 1; overflow: hidden; display: flex; flex-direction: column; }
.week-scroll { flex: 1; overflow-x: auto; overflow-y: auto; cursor: grab; -webkit-overflow-scrolling: touch; }
.week-scroll.is-panning { cursor: grabbing; }
.week-scroll::-webkit-scrollbar { height: 5px; width: 4px; }
.week-scroll::-webkit-scrollbar-thumb { background: #d0d5df; border-radius: 4px; }
.week-inner { min-width: 860px; display: flex; flex-direction: column; background: #fff; }

.wk-header-row { display: flex; border-bottom: 1px solid #e2e6ed; flex-shrink: 0; background: #f8f9fb; }
.wk-corner { width: 160px; flex-shrink: 0; display: flex; align-items: center; padding: 10px 12px; border-right: 1px solid #e2e6ed; }
.wk-day-head { flex: 1; min-width: 110px; padding: 8px 6px 10px; border-right: 1px solid #e2e6ed; text-align: center; cursor: pointer; transition: background 0.12s; }
.wk-day-head:last-child { border-right: none; }
.wk-day-head:hover { background: #f0f8ff; }
.wkh-today   { background: #eff6ff; }
.wkh-weekend { background: #fafafa; }
.wkh-name { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: #9ba3b2; }
.wkh-num  { font-size: 18px; font-weight: 700; color: #1a1d27; line-height: 1.2; margin: 2px 0; }
.wkh-today-badge { color: #3b82f6; }
.wkh-slot-labels { display: flex; justify-content: center; gap: 6px; }
.wkh-slot-lbl { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.morning-lbl   { color: #d97706; }
.afternoon-lbl { color: #3b82f6; }

.wk-tech-row { display: flex; border-bottom: 1px solid #edf0f4; min-height: 150px; }
.wk-tech-label { width: 160px; flex-shrink: 0; display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-right: 1px solid #e2e6ed; border-left: 3px solid transparent; background: #fff; }

.wk-day-cell { flex: 1; min-width: 110px; display: flex; flex-direction: column; border-right: 1px solid #edf0f4; cursor: pointer; position: relative; }
.wk-halves { display: flex; flex: 1; }
.wk-day-select-btn {
    width: 100%; border: none; cursor: pointer;
    background: #f1f3f9; color: #6366f1;
    display: flex; align-items: center; justify-content: center;
    gap: 4px; padding: 5px 6px;
    font-size: 10px; font-weight: 700; font-family: 'Figtree', sans-serif;
    letter-spacing: 0.3px; transition: all 0.15s;
    border-top: 1px solid #e2e6ed; flex-shrink: 0;
}
.wk-day-select-btn:hover { background: #4f46e5; color: #fff; }
.wk-day-select-btn.wk-day-select-all { background: #4f46e5; color: #fff; }
.wk-day-select-btn.wk-day-select-all:hover { background: #ef4444; }
.wk-day-cell:last-child { border-right: none; }
.wkc-today   { background: #f8fbff; }
.wkc-weekend { background: #fafafa; }

.wk-half { flex: 1; display: flex; flex-direction: column; position: relative; overflow: hidden; }
.morning-half   { background: #fffdf7; border-right: 1px solid #f5e8c0; }
.afternoon-half { background: #f8fbff; }
.morning-half.cell-busy   { background: #fffaed; }
.afternoon-half.cell-busy { background: #eef6ff; }
.wk-half.cell-overload    { background: #fff5f5 !important; }

/* ── Multi-slot sélectionné (vue semaine) ── */
.wk-half.ms-selected-half { background: #eef2ff !important; outline: 2px solid #6366f1; outline-offset: -2px; }

.ms-wk-overlay {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    z-index: 4; pointer-events: none;
    background: rgba(99,102,241,0.07);
}
.ms-wk-check {
    width: 20px; height: 20px; border-radius: 50%;
    background: #4f46e5; color: #fff; font-size: 10px;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 1px 6px rgba(79,70,229,0.35);
    animation: ms-pop 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.wk-sat-bar { height: 4px; width: 100%; background: #edf0f4; flex-shrink: 0; }
.wk-sat-fill { height: 100%; transition: width 0.4s ease, background 0.3s; }

.wk-chips { flex: 1; display: flex; flex-direction: column; gap: 2px; padding: 4px 4px 0; }
.wk-chip { border-left: 2px solid; border-radius: 4px; padding: 3px 5px; cursor: pointer; display: flex; align-items: center; gap: 3px; animation: ev-appear 0.18s ease; transition: filter 0.12s, transform 0.1s, box-shadow 0.12s; }
.wk-half:hover .wk-chip { filter: brightness(0.93); transform: translateY(-1px); box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
.wk-chip:hover { filter: brightness(0.85) !important; transform: translateY(-2px) !important; }
.wk-chip.ev-preview { border-style: dashed; animation: preview-blink 1.3s ease-in-out infinite; }
.wk-chip-title { font-size: 10.5px; font-weight: 500; color: #1a1d27; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.wk-add-btn { flex-shrink: 0; margin: 3px auto 4px; width: 22px; height: 22px; border-radius: 50%; border: none; background: #e8edf5; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 10px; transition: all 0.15s; }
.wk-half:hover .wk-add-btn { background: #3b82f6; color: #fff; transform: scale(1.1); box-shadow: 0 2px 6px rgba(59,130,246,0.35); }

.wk-overload-flag { position: absolute; bottom: 3px; right: 3px; font-size: 9px; color: #ef4444; }

/* ══════════════════════════════════════════
   VUE MOIS
══════════════════════════════════════════ */
.view-month { flex: 1; overflow-y: auto; padding: 14px; background: #f0f2f5; }
.month-weekdays { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; margin-bottom: 4px; }
.month-wd { text-align: center; font-size: 10.5px; font-weight: 700; color: #9ba3b2; padding: 6px 0; }
.month-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
.month-cell { background: #fff; border: 1px solid #e2e6ed; border-radius: 10px; min-height: 88px; padding: 7px; cursor: pointer; position: relative; transition: all 0.14s; overflow: hidden; }
.month-cell:hover:not(.mc-empty) { border-color: #3b82f6; box-shadow: 0 3px 12px #3b82f615; transform: translateY(-1px); }
.mc-empty { background: transparent; border-color: transparent; pointer-events: none; }
.mc-today { background: #eff6ff; border-color: #93c5fd; }
.mc-selected { border-color: #3b82f6; box-shadow: 0 0 0 2px #3b82f625; }
.mc-weekend { background: #fafafa; }
.mc-has-overload { border-color: #fca5a5 !important; }
.mc-num { font-size: 13px; font-weight: 700; color: #374151; margin-bottom: 5px; display: inline-flex; align-items: center; justify-content: center; min-width: 24px; min-height: 24px; }
.mc-today-badge { background: #3b82f6; color: #fff !important; border-radius: 50%; width: 24px; height: 24px; font-size: 12px; }
.mc-tech-rows { display: flex; flex-direction: column; gap: 3px; }
.mc-tech-row { display: flex; align-items: center; gap: 4px; }
.mc-tech-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.mc-slot-pips { display: flex; gap: 2px; flex: 1; }
.mc-slot-pip { flex: 1; height: 8px; border-radius: 2px; background: #edf0f4; transition: all 0.2s; }
.mc-slot-pip.pip-overload { background: #ef4444 !important; animation: overload-pulse 0.9s ease-in-out infinite; }
@keyframes overload-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
.mc-slot-pip.pip-preview { animation: preview-blink 1.3s ease-in-out infinite; }
.mc-slot-pip.pip-ms-selected { background: #6366f1 !important; box-shadow: 0 0 0 1.5px #4f46e5; }
.mc-more { font-size: 9px; color: #9ba3b2; }
.mc-overload-flag { position: absolute; top: 5px; right: 22px; font-size: 10px; color: #ef4444; }
.mc-preview-star { position: absolute; top: 5px; right: 6px; font-size: 10px; color: #f59e0b; }

/* ══ TECH FILTER SELECT ══ */
.tb-tech-filter { position: relative; }
.custom-select { position: relative; user-select: none; font-size: 0.875rem; }
.cs-trigger { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; min-width: 200px; white-space: nowrap; }
.custom-select:hover .cs-trigger, .custom-select.open .cs-trigger { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12); }
.cs-arrow { margin-left: auto; font-size: 0.7rem; color: #94a3b8; transition: transform 0.22s ease; }
.custom-select.open .cs-arrow { transform: rotate(180deg); }
.cs-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; display: inline-block; }
.cs-dot-all { background: linear-gradient(135deg, #6366f1, #ec4899, #f59e0b); }
.cs-label { color: #1e293b; font-weight: 500; }

.cs-dropdown-portal { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.13); overflow: hidden; animation: cs-drop-in 0.16s ease; }
@keyframes cs-drop-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.cs-dropdown-portal .cs-option { display: flex; align-items: center; gap: 10px; padding: 9px 14px; cursor: pointer; color: #334155; font-size: 0.875rem; font-family: 'Figtree', sans-serif; transition: background 0.1s; white-space: nowrap; }
.cs-dropdown-portal .cs-option:hover { background: #f1f5f9; }
.cs-dropdown-portal .cs-option.selected { background: #eef2ff; color: #4f46e5; font-weight: 600; }
.cs-dropdown-portal .cs-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; display: inline-block; }
.cs-dropdown-portal .cs-dot-all { background: linear-gradient(135deg, #6366f1, #ec4899, #f59e0b); }
.cs-dropdown-portal .cs-option.selected .cs-dot { box-shadow: 0 0 0 2px #fff, 0 0 0 3.5px #4f46e5; }

/* ── Date Navigator ── */
.tb-date-nav { display: flex; align-items: center; gap: 2px; margin-left: auto; }
.tb-date-prev, .tb-date-next { width: 28px; height: 28px; border-radius: 7px; border: 1.5px solid #e2e8f0; background: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #64748b; font-size: 10px; transition: all 0.12s; }
.tb-date-prev:hover, .tb-date-next:hover { border-color: #6366f1; color: #6366f1; background: #eef2ff; }
.tb-date-trigger { display: flex; align-items: center; gap: 7px; padding: 5px 11px; border-radius: 8px; border: 1.5px solid #e2e8f0; background: #fff; cursor: pointer; font-family: 'Figtree', sans-serif; font-size: 12.5px; font-weight: 600; color: #1e293b; transition: all 0.15s; white-space: nowrap; }
.tb-date-trigger:hover { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
.tb-date-icon  { color: #6366f1; font-size: 12px; }
.tb-date-arrow { font-size: 9px; color: #94a3b8; transition: transform 0.2s; }
.tb-date-arrow.open { transform: rotate(180deg); }

/* ── Picker portal ── */
.dp-portal { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 14px; box-shadow: 0 12px 32px rgba(0,0,0,0.12); padding: 14px; width: 260px; animation: dp-in 0.15s ease; font-family: 'Figtree', sans-serif; }
@keyframes dp-in { from { opacity:0; transform: translateY(-6px); } to { opacity:1; transform: translateY(0); } }
.dp-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.dp-header-center { display: flex; gap: 4px; }
.dp-title { font-size: 14px; font-weight: 700; color: #1e293b; }
.dp-nav { width: 26px; height: 26px; border-radius: 6px; border: 1px solid #e2e8f0; background: transparent; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #64748b; font-size: 10px; transition: all 0.1s; }
.dp-nav:hover { background: #eef2ff; border-color: #6366f1; color: #6366f1; }
.dp-select { font-size: 12px; font-weight: 600; color: #1e293b; border: 1px solid #e2e8f0; border-radius: 6px; padding: 3px 6px; background: #f8fafc; cursor: pointer; font-family: 'Figtree', sans-serif; }
.dp-select:focus { outline: none; border-color: #6366f1; }
.dp-weekdays { display: grid; grid-template-columns: repeat(7, 1fr); text-align: center; margin-bottom: 4px; }
.dp-weekdays span { font-size: 10px; font-weight: 700; color: #94a3b8; padding: 4px 0; }
.dp-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.dp-empty { height: 32px; }
.dp-day { height: 32px; border-radius: 7px; border: none; background: transparent; font-size: 12.5px; font-weight: 500; color: #334155; cursor: pointer; transition: all 0.1s; font-family: 'Figtree', sans-serif; }
.dp-day:hover { background: #eef2ff; color: #4f46e5; }
.dp-day.today { color: #4f46e5; font-weight: 800; }
.dp-day.selected { background: #4f46e5 !important; color: #fff !important; border-radius: 7px; }
.dp-day.in-week { background: #eef2ff; color: #4f46e5; border-radius: 0; }
.dp-day.in-week:first-of-type, .dp-day.selected { border-radius: 7px 0 0 7px; }
.dp-day.in-week:last-of-type { border-radius: 0 7px 7px 0; }
.dp-day.selected.in-week { border-radius: 7px; }
.dp-footer { margin-top: 10px; text-align: center; }
.dp-today-btn { font-size: 11.5px; font-weight: 600; color: #6366f1; background: #eef2ff; border: none; border-radius: 6px; padding: 5px 14px; cursor: pointer; transition: background 0.1s; font-family: 'Figtree', sans-serif; }
.dp-today-btn:hover { background: #e0e7ff; }
.dp-months { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.dp-month-btn { padding: 9px 4px; border-radius: 8px; border: 1px solid transparent; background: transparent; font-size: 12.5px; font-weight: 500; color: #334155; cursor: pointer; transition: all 0.1s; font-family: 'Figtree', sans-serif; }
.dp-month-btn:hover { background: #eef2ff; color: #4f46e5; }
.dp-month-btn.active { background: #4f46e5; color: #fff; }
</style>